import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import esLocale from 'date-fns/locale/es';
import enLocale from 'date-fns/locale/en-US';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Company, FormType, Location } from '~/core/types';
import { useForm as useFormHook } from '~/hooks/useForm';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~/hooks/useAuth';
import { doc, getDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import firebaseFirestore from '~/config/firebase/firestore/firestore';
import useFetchReservation from '~/hooks/useFetchReservation';
import useGetParam from '~/hooks/useGetParam';
import {
  formatDate,
  formatHourWithPeriod,
  getTimestampUtcToZonedTime,
} from '~/utils/date';
import ReservationSummary from './ReservationSummary';
import AgregarACalendario from '../calendario/AgregarACalendario';
import { useActivityLogs } from '~/hooks/useActivityLogs';
import { releaseSlots } from '../repositories/occupancyRepository';
import {
  registrarEvento,
  describirActor,
  idDeEmpresa,
} from '~/core/tracking/reservationEvents';

// Declare the global gtag_report_conversion function
declare global {
  interface Window {
    gtag_report_conversion: (url?: string) => boolean;
  }
}

export type FormSuccesfullSentProps = {
  onBack?: () => void;
  reservationId: string;
};
const FormSuccesfullSent = ({ reservationId, onBack }: FormSuccesfullSentProps) => {
  const { onToggle, isOpen, onClose } = useDisclosure();
  const [isCanceling, setIsCanceling] = useState(false);
  const { changeForm } = useFormHook();
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  // El panel abre este formulario con `from=sistema`.
  const from = useGetParam('from');
  const textSuccess = useRef<HTMLParagraphElement>(null);
  const reservation = useFetchReservation(reservationId);
  // New state to hold company data
  const [company, setCompany] = useState<Company>();
  const { logActivity } = useActivityLogs();

  useEffect(() => {
    // Saltar al mensaje solo tiene sentido en el formulario público, donde el
    // cliente acaba de enviar y necesita ver la confirmación. Dentro del panel
    // desorienta: quien atiende se queda mirando el botón que pulsó y, si la
    // pantalla se mueve sola, no sabe si pasó algo. Allí basta con el
    // indicador de carga del propio botón.
    if (auth.user?.uid) return;
    if (textSuccess.current) {
      textSuccess.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [textSuccess, auth.user?.uid]);

  const handleCancelReservation = useCallback(async () => {
    setIsCanceling(true);
    onClose!();

    try {
      // Validate required data
      if (!reservation?.id) {
        throw new Error('Reservation ID is required');
      }

      if (!reservation?.location || typeof reservation.location !== 'object') {
        throw new Error('Invalid location data');
      }

      if (!reservation?.startDatetime) {
        throw new Error('Start datetime is required');
      }

      const reservationId = reservation.id;
      const locationId = (reservation.location as Location).id;

      if (!locationId) {
        throw new Error('Location ID is required');
      }

      // Update location's reservations data structure
      const locationRef = doc(firebaseFirestore, 'locations', locationId);
      const locationDoc = await getDoc(locationRef);

      if (!locationDoc.exists()) {
        throw new Error('Location not found');
      }

      const locationData = locationDoc.data() as Location;
      const reservations = locationData.reservations ?? {};

      const formattedDate = format((reservation.startDatetime as Timestamp).toDate(), 'yyyy-MM-dd');

      // Store before state for logging
      const beforeUpdate = JSON.parse(JSON.stringify(reservations));

      // Franjas que ocupaba, solo para el registro de actividad.
      const hoursCleared: string[] = Object.entries(reservations[formattedDate] ?? {})
        .filter(([, ids]) => Array.isArray(ids) && ids.includes(reservationId))
        .map(([hour]) => hour);

      // Libera TODAS sus plazas en una transacción. Antes se quitaba una sola
      // aparición del id por franja, así que una reserva de N personas en
      // coworking dejaba N-1 plazas bloqueadas para siempre.
      await releaseSlots(locationId, [formattedDate], reservationId);

      // Update reservation status
      const reservationRef = doc(firebaseFirestore, 'reservations', reservationId);
      await updateDoc(reservationRef, {
        status: 'cancelled',
        cancelledBy: auth.user?.uid ? 'system' : 'customer',
        // `cancelledBy` solo distingue 'system' de 'customer'. Esto guarda
        // además quién fue exactamente, que es lo que hacía falta para saber
        // de verdad el autor de una cancelación.
        cancelledByUser: describirActor(auth.user),
        // Cancelar no dejaba ninguna marca de tiempo: `updatedAt` se quedaba
        // como estaba y luego era imposible saber cuándo ocurrió.
        cancelledAt: new Date(),
        updatedAt: Date.now(),
      });

      // Log comprehensive cancellation with focused locationReservationsData
      await logActivity({
        activityType: 'reservation_cancellation_complete',
        entityId: reservationId,
        entityType: 'reservation',
        locationId: locationId,
        details: {
          processedAt: new Date().toISOString(),
          reservationCode: reservation?.code || '',
          customerName: reservation?.name || reservation?.namesAndSurnames || 'Unknown',
          startDate: formattedDate,
          startHour: format((reservation.startDatetime as Timestamp).toDate(), 'HH:mm'),
          endHour: reservation?.endDatetime
            ? format((reservation.endDatetime as Timestamp).toDate(), 'HH:mm')
            : undefined,
          numberPeople: reservation?.numberPeople || 0,
          cancelledBy: auth.user?.uid ? 'system' : 'customer',
          cancellationMethod: 'success_form',
          locationReservationsData: {
            locationId: locationId,
            affectedDate: formattedDate,
            hoursCleared: hoursCleared,
            beforeUpdate: {
              [formattedDate]: beforeUpdate[formattedDate] ?? [],
            },
            afterUpdate: {
              [formattedDate]: reservations[formattedDate] ?? [],
            },
          },
          summary: {
            wasSuccessful: true,
            totalHoursCleared: hoursCleared.length,
            cancellationSource: 'success_form',
          },
          systemInfo: {
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent,
          },
        },
      });

      await registrarEvento({
        tipo: 'cancelled',
        reservationId,
        code: reservation?.code,
        companyId: idDeEmpresa((reservation?.location as Location)?.company),
        locationId,
        locationName: locationData?.name,
        usuario: auth.user,
        origen: { from },
        cliente: {
          nombre: reservation?.namesAndSurnames ?? reservation?.name,
          correo: reservation?.email,
        },
      });

      toast.success(t('general.text_cancelled_reservation') ?? '');
      onBack!();
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      toast.error('Error cancelling reservation');
    } finally {
      setIsCanceling(false);
    }
  }, [reservation, auth, logActivity, onBack, onClose, t]);

  const getDay = useMemo(() => {
    if (!reservation) return '';
    // get day from startDatetime
    const date = (reservation?.startDatetime as Timestamp)?.toDate();

    return formatDate(
      getTimestampUtcToZonedTime(date),
      'dd MMMM yyyy',
      i18n.language == 'es' ? esLocale : enLocale
    );
  }, [reservation]);

  const getStarHour = useMemo(() => {
    if (!reservation) return '';
    // get start hour from startDatetime
    const date = (reservation?.startDatetime as Timestamp)?.toDate();

    return formatHourWithPeriod(date) ?? new Date();
  }, [reservation]);

  const getEndHour = useMemo(() => {
    if (!reservation) return '';
    if (!reservation?.endDatetime) {
      return '';
    }
    // get end hour from startDatetime
    const date = (reservation?.endDatetime as Timestamp)?.toDate() ?? new Date();

    return `a ${formatHourWithPeriod(date)}`;
  }, [reservation]);

  // Fetch company data once the reservation is available
  useEffect(() => {
    const fetchCompany = async () => {
      if (reservation && reservation?.location?.company) {
        const companySnapshot = await getDoc(reservation?.location?.company as any);
        if (companySnapshot.exists()) {
          const companyData = companySnapshot.data();
          console.log(companyData);
          if (companyData && typeof companyData === 'object') {
            setCompany({ id: companySnapshot.id, ...companyData } as Company);
          }
        }
      }
    };
    fetchCompany();
  }, [reservation]);

  // Track conversion when component mounts
  useEffect(() => {
    // Match the TestForm implementation - call the function directly
    if (typeof window !== 'undefined') {
      try {
        window.gtag_report_conversion();
      } catch (error) {
        console.error('Error calling gtag_report_conversion:', error);
      }
    }
  }, []);

  if (!company) {
    return (
      <VStack alignItems='center' justifyContent='center'>
        <Spinner size={'xl'} />;
      </VStack>
    );
  }

  if (company?.externalId === 'noi-remb') {
    return (
      <VStack ref={textSuccess} alignItems='center' justifyContent='center'>
        <ReservationSummary
          reservation={reservation}
          reservationId={reservationId}
          company={company}
          goBack={() => {
            onBack!();
            changeForm(FormType.NEW_RESERVATION);
          }}
          cancelReservation={handleCancelReservation}
        />
      </VStack>
    );
  } else {
    return (
      <VStack ref={textSuccess} alignItems='center' justifyContent='center'>
        <Text textAlign={'center'} fontWeight={'bold'}>
          {t('reserve_confirmation.title_chunk_1')} {reservation?.namesAndSurnames as string}{' '}
          {t('reserve_confirmation.title_chunk_2')} #{reservation?.code as string}{' '}
          {t('reserve_confirmation.title_chunk_3')}
          {' ' + getDay + ' ' + getStarHour + ' ' + getEndHour}
        </Text>
        <Text textAlign={'center'}>{t('reserve_confirmation.text_email_sent')} </Text>
        <Box pt={8} />
        <HStack>
          <Button
            onClick={() => {
              onBack!();

              changeForm(FormType.NEW_RESERVATION);
            }}
          >
            {t('general.text_return')}
          </Button>
          {/* Solo si la empresa lo tiene activado; si no, no pinta nada. */}
          <AgregarACalendario
            reservation={reservation}
            reservationId={reservationId}
            company={company}
            botonProps={{ colorScheme: 'blue', variant: 'outline' }}
          />
          <Popover isOpen={isOpen} onClose={onClose}>
            <PopoverTrigger>
              <Button onClick={onToggle} colorScheme='red'>
                {t('general.cancel_reservation')}
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <PopoverHeader fontWeight='semibold'>{t('general.cancel_reservation')}</PopoverHeader>
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverBody>{t('reservation_form.question_cancel')}</PopoverBody>
              <PopoverFooter display='flex' justifyContent='flex-end'>
                <ButtonGroup size='sm'>
                  <Button onClick={onToggle} colorScheme='red'>
                    {t('general.text_not_close')}
                  </Button>
                  <Button
                    isLoading={isCanceling}
                    loadingText='Submitting'
                    onClick={handleCancelReservation}
                    variant='outline'
                  >
                    {t('general.text_yes_cancel')}
                  </Button>
                </ButtonGroup>
              </PopoverFooter>
            </PopoverContent>
          </Popover>
        </HStack>
      </VStack>
    );
  }
};

export default FormSuccesfullSent;
